import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeactivationReason, DriverSuspension } from '../../models';
import { DriverSuspensionDetail } from './../../models/driver-suspension-detail';

@Injectable({
  providedIn: 'root'
})
export class DriverSuspensionService extends UzurvRestService {
  readonly resourceUrl = 'driverSuspensions/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of driver suspensions
   *
   * @param driverId
   * @permission driver_suspension_read
   */
  getDriverSuspensions(
    driverId: number,
    pageNumber = 0,
    pageSize = 20,
    includeInactive = false
  ): Observable<Array<DriverSuspensionDetail>> {
    return this.get<DriverSuspensionDetail>(`${this.resourceUrl}drivers/${driverId}/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      includeInactive: includeInactive
    }).pipe(map(response => Deserialize(response, DriverSuspensionDetail)));
  }

  /**
   * Creates a new driver suspension with the provided object
   *
   * @param driverSuspension
   * @permission driver_suspension_create
   */
  createDriverSuspension(driverSuspension: DriverSuspension): Observable<DriverSuspension> {
    return this.post<DriverSuspension>(this.resourceUrl, Serialize(driverSuspension)).pipe(
      map(response => Deserialize(response, DriverSuspension))
    );
  }

  /**
   * Updates an existing driver suspension with the provided object
   *
   * @param driverSuspension
   * @permission driver_suspension_update
   */
  updateDriverSuspension(driverSuspension: DriverSuspension): Observable<DriverSuspension> {
    return this.put<DriverSuspension>(`${this.resourceUrl}${driverSuspension.id}`, Serialize(driverSuspension)).pipe(
      map(response => Deserialize(response, DriverSuspension))
    );
  }

  /**
   * Removes a driver suspension by ID
   *
   * @param driverSuspensionId
   * @permission driver_suspension_delete
   */
  removeDriverSuspension(driverSuspensionId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${driverSuspensionId}`);
  }

  // Utils

  /**
   * Finds effective driver suspensions in an array of driver suspensions
   *
   * @param driverSuspensions
   */
  findEffectiveDriverSuspensions(driverSuspensions: Array<DriverSuspensionDetail> = []): Array<DriverSuspensionDetail> {
    return driverSuspensions.filter(ds => ds.effective);
  }

  /**
   * Checks array of driver suspensions to see if any are effective
   *
   * @param driverSuspensions
   */
  areAnyDriverSuspensionsEffective(driverSuspensions: Array<DriverSuspensionDetail> = []): boolean {
    return this.findEffectiveDriverSuspensions(driverSuspensions).length > 0;
  }

  filterDeactivationReasonsForDriverSuspensions(
    deactivationReasons: Array<DeactivationReason> = [],
    driverSuspensions: Array<DriverSuspension> = []
  ): Array<DeactivationReason> {
    return deactivationReasons.filter(dr => driverSuspensions.some(ds => ds.deactivationReason.id === dr.id));
  }
}
